/**
 * @file OpenTelemetry Fetch Success Handler
 * @description Handles successful HTTP fetch responses by logging, adding OpenTelemetry events, setting span attributes, and recording metrics.
 */

import { EVENTS, SEVERITY_TEXT, STAGES } from '../../constants';
import { log } from '../../logging';
import { getDynamicHttpLogAttributes } from '../../utils/getDynamicHttpLogAttributes';
import { SEMATTRS_HTTP_STATUS_CODE } from '@opentelemetry/semantic-conventions';
import { HTTP_STATUS_TEXT } from '../../constants/attributeNames';
import { SpanStatusCode } from '@opentelemetry/api';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { calculateSizeInBytes, getDuration, getIdsFromSpan } from '../../utils';
import { otlHandleHttpResponseMetrics } from '../../utils/otlHandleHttpResponseMetrics';

/**
 * Handles a successful HTTP fetch response, adding OpenTelemetry events, logging, and recording metrics.
 *
 * @param {Object} span - The OpenTelemetry span associated with the request.
 * @param {string} url - The URL of the HTTP request.
 * @param {string} method - The HTTP method used in the request (e.g., 'GET', 'POST').
 * @param {Object} response - The response object from the fetch request.
 * @param {number} response.status - The HTTP response status code.
 * @param {string} response.statusText - The HTTP response status text.
 * @param {Object} response.responseData - The response data received from the request.
 */
function otlFetchSuccess(span, url, method, response) {
    const { status, statusText, responseData: data } = response;
    const dataSizeInBytes = calculateSizeInBytes(data);
    span.addEvent(EVENTS.RESPONSE_RECEIVED);

    log(`Request to ${url} succeeded`, SEVERITY_TEXT.INFO, {
        url,
        status,
        ...getIdsFromSpan(span),
        ...getDynamicHttpLogAttributes({ url, stage: STAGES.RESPONSE, data, dataSizeInBytes }),
    });

    span.setAttributes({
        [SEMATTRS_HTTP_STATUS_CODE]: status,
        [HTTP_STATUS_TEXT]: statusText,
    });
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();

    otlHandleHttpResponseMetrics({
        method,
        url,
        duration: getDuration(span),
        status,
        dataSizeInBytes,
    });
}

export default withSafelyExecute(otlFetchSuccess);
